/**
 * Criteria View Component
 * 
 * Primary view showing all evaluation criteria with full details.
 * This is the main tab users should see - criteria are what matter for proposals.
 * 
 * Features:
 * - Filter by type (gate vs scored)
 * - Search by title/description
 * - Sort by points, page, etc.
 * - Expand to see questions and mapped sections
 * - Shows which RFP sections each criterion comes from
 */

import React, { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Award,
  CheckCircle,
  FileText,
  MessageSquare,
  MapPin,
  SortAsc,
  SortDesc,
} from 'lucide-react';
import type { EnrichedCriterion } from '../../hooks/useProductionTemplate';
import { formatQuestionTextForDisplay } from '../../utils/formatQuestionText';

interface CriteriaViewProps {
  criteria: EnrichedCriterion[];
}

type FilterType = 'all' | 'gate' | 'scored';
type SortField = 'id' | 'title' | 'points' | 'page' | 'questions';
type SortDirection = 'asc' | 'desc';

export function CriteriaView({ criteria }: CriteriaViewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedCriteria, setExpandedCriteria] = useState<Set<string>>(new Set());

  // Filter and sort criteria
  const filteredAndSortedCriteria = useMemo(() => {
    let filtered = [...criteria];

    // Apply type filter (handle both 'type' and 'kind' fields)
    if (filterType !== 'all') {
      filtered = filtered.filter(c => (c.type || c.kind) === filterType);
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        c =>
          (c.title || '').toLowerCase().includes(term) ||
          (c.description || '').toLowerCase().includes(term) ||
          (c.id || c.criterion_id || '').toLowerCase().includes(term)
      );
    }

    // Apply sort
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortField) {
        case 'id':
          aVal = a.id || a.criterion_id || '';
          bVal = b.id || b.criterion_id || '';
          break;
        case 'title':
          aVal = (a.title || '').toLowerCase();
          bVal = (b.title || '').toLowerCase();
          break;
        case 'points':
          aVal = a.points || a.weight || 0;
          bVal = b.points || b.weight || 0;
          break;
        case 'page':
          // Handle character position (stored as 'page' field)
          aVal = a.page || 0;
          bVal = b.page || 0;
          break;
        case 'questions':
          aVal = a.question_count || (a.questions || []).length || 0;
          bVal = b.question_count || (b.questions || []).length || 0;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return filtered;
  }, [criteria, searchTerm, filterType, sortField, sortDirection]);

  const toggleCriterion = (id: string) => {
    const newExpanded = new Set(expandedCriteria);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCriteria(newExpanded);
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Stats (handle both 'type' and 'kind' fields)
  const gateCriteria = criteria.filter(c => (c.type || c.kind) === 'gate');
  const scoredCriteria = criteria.filter(c => (c.type || c.kind) === 'scored');
  const totalPoints = scoredCriteria.reduce((sum, c) => sum + (c.points || c.weight || 0), 0);

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Award className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <p className="text-xs font-medium text-gray-600 dark:text-theme-text-secondary">
              Total Criteria
            </p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-theme-text">
            {criteria.length}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <p className="text-xs font-medium text-gray-600 dark:text-theme-text-secondary">
              Gate (Mandatory)
            </p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-theme-text">
            {gateCriteria.length}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Award className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <p className="text-xs font-medium text-gray-600 dark:text-theme-text-secondary">
              Scored
            </p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-theme-text">
            {scoredCriteria.length}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Award className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            <p className="text-xs font-medium text-gray-600 dark:text-theme-text-secondary">
              Total Points
            </p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-theme-text">
            {totalPoints}
          </p>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex gap-3">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search criteria by title, description, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            All ({criteria.length})
          </button>
          <button
            onClick={() => setFilterType('gate')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'gate'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Gate ({gateCriteria.length})
          </button>
          <button
            onClick={() => setFilterType('scored')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'scored'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Scored ({scoredCriteria.length})
          </button>
        </div>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center gap-2 text-sm">
        <Filter className="h-4 w-4 text-gray-500" />
        <span className="text-gray-600 dark:text-gray-400">Sort by:</span>
        {([
          { field: 'id', label: 'ID' },
          { field: 'title', label: 'Title' },
          { field: 'points', label: 'Points' },
          { field: 'page', label: 'Char Pos' },
          { field: 'questions', label: 'Questions' }
        ] as { field: SortField; label: string }[]).map(({ field, label }) => (
          <button
            key={field}
            onClick={() => toggleSort(field)}
            className={`px-3 py-1 rounded flex items-center gap-1 ${
              sortField === field
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            {label}
            {sortField === field && (
              sortDirection === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
            )}
          </button>
        ))}
      </div>

      {/* Criteria List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {filteredAndSortedCriteria.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-theme-text-secondary">
            No criteria found matching your filters
          </div>
        ) : (
          filteredAndSortedCriteria.map((criterion) => {
            const criterionId = criterion.id || criterion.criterion_id || '';
            const isExpanded = expandedCriteria.has(criterionId);
            const isGate = (criterion.type || criterion.kind) === 'gate';
            const points = criterion.points || criterion.weight || 0;
            const questionCount = criterion.question_count || (criterion.questions || []).length || 0;
            const charPos = criterion.page || 0;

            return (
              <div
                key={criterionId}
                className={`border rounded-lg overflow-hidden ${
                  isGate
                    ? 'border-green-200 dark:border-green-800'
                    : 'border-purple-200 dark:border-purple-800'
                }`}
              >
                {/* Criterion Header */}
                <button
                  onClick={() => toggleCriterion(criterionId)}
                  className="w-full flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                >
                  {/* Expand Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>

                  {/* Criterion ID Badge */}
                  <div className="flex-shrink-0">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isGate
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                      }`}
                    >
                      {criterionId}
                    </span>
                  </div>

                  {/* Criterion Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-theme-text mb-1">
                      {(criterion.title || 'Untitled Criterion').substring(0, 150)}
                      {(criterion.title || '').length > 150 ? '...' : ''}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-theme-text-secondary line-clamp-2">
                      {criterion.description || 'No description'}
                    </p>
                  </div>

                  {/* Criterion Stats */}
                  <div className="flex-shrink-0 flex flex-col items-end gap-1">
                    {!isGate && points > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                        <Award className="h-3 w-3" />
                        {points} pts
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs text-gray-600 dark:text-gray-400">
                      <MessageSquare className="h-3 w-3" />
                      {questionCount} Q
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs text-gray-600 dark:text-gray-400">
                      <FileText className="h-3 w-3" />
                      @{charPos}
                    </span>
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                    {/* Character Position */}
                    {charPos > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-theme-text-secondary mb-1">
                          Character Position:
                        </p>
                        <p className="text-sm text-gray-900 dark:text-theme-text">
                          @{charPos} (approx. page {Math.floor(charPos / 3000) + 1})
                        </p>
                      </div>
                    )}

                    {/* Source Section */}
                    {criterion.source_section && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-theme-text-secondary mb-1">
                          Source Section:
                        </p>
                        <p className="text-sm text-gray-900 dark:text-theme-text">
                          {criterion.source_section}
                        </p>
                      </div>
                    )}

                    {/* Mapped Sections */}
                    {criterion.mapped_sections && criterion.mapped_sections.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-theme-text-secondary mb-1">
                          Found in RFP Sections:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {criterion.mapped_sections.map((section: any, idx: number) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                            >
                              <MapPin className="h-3 w-3" />
                              {typeof section === 'string' ? section : (section.title || section.name)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Questions */}
                    {criterion.questions && criterion.questions.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-theme-text-secondary mb-2">
                          Questions ({criterion.questions.length}):
                        </p>
                        <div className="space-y-2">
                          {criterion.questions.map((question, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-2 p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700"
                            >
                              <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 text-xs font-medium text-blue-700 dark:text-blue-300">
                                {idx + 1}
                              </span>
                              <p className="text-sm text-gray-900 dark:text-theme-text flex-1">
                                {question.question || question.text}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
